import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useReaderAuth } from '../context/ReaderAuthContext';
import { Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Handles the Emergent Google OAuth callback.
 * Reads session_id from URL hash, exchanges it for a reader JWT, then redirects.
 */
const ReaderGoogleCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithGoogle } = useReaderAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double-execution under React StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const exchangeSession = async () => {
      // Parse session_id from URL fragment
      const hash = location.hash || window.location.hash;
      const params = new URLSearchParams(hash.replace(/^#/, ''));
      const sessionId = params.get('session_id');

      if (!sessionId) {
        navigate('/signin', { replace: true });
        return;
      }

      // Clear the hash immediately
      window.history.replaceState(null, '', window.location.pathname);

      try {
        const res = await fetch(`${API_URL}/api/reader/google-auth`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: sessionId }),
        });

        if (!res.ok) {
          throw new Error('Google sign-in failed');
        }

        const data = await res.json();
        loginWithGoogle(data);

        // Honour pre-auth redirect if stored
        const intended = sessionStorage.getItem('reader_auth_redirect');
        sessionStorage.removeItem('reader_auth_redirect');

        if (intended === 'buy') {
          window.location.href = 'https://buy.stripe.com/6oUcN4etP3qP4kZfC38so01';
        } else if (intended) {
          navigate(intended, { replace: true });
        } else {
          navigate('/courses', { replace: true, state: { user: data.user } });
        }
      } catch (err) {
        console.error('Google auth error:', err);
        navigate('/signin?error=google_failed', { replace: true });
      }
    };

    exchangeSession();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-[#faf8f5] flex flex-col items-center justify-center gap-4">
      <Loader2 className="w-10 h-10 text-[#d4af37] animate-spin" />
      <p className="text-[#5a6c7d] text-sm font-sans tracking-wide">Signing you in with Google…</p>
    </div>
  );
};

export default ReaderGoogleCallback;
